import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

// A techy, interactive neural-network visual (mouse-reactive parallax + animated signals).
const LAYERS = [2, 4, 4, 3];
const W = 520;
const H = 460;
const PAD_X = 70;
const PAD_Y = 60;

const buildNodes = () => {
  const cols = LAYERS.length;
  const nodes = [];
  LAYERS.forEach((count, ci) => {
    const x = PAD_X + (ci * (W - PAD_X * 2)) / (cols - 1);
    for (let i = 0; i < count; i++) {
      const y = PAD_Y + ((i + 0.5) * (H - PAD_Y * 2)) / count;
      nodes.push({ id: `${ci}-${i}`, x, y, layer: ci });
    }
  });
  return nodes;
};

const AINetwork = () => {
  const nodes = buildNodes();
  const edges = [];
  nodes.forEach((a) => {
    nodes
      .filter((b) => b.layer === a.layer + 1)
      .forEach((b) => edges.push({ a, b, key: `${a.id}_${b.id}` }));
  });

  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="ai-network"
      className="relative w-full h-[420px] md:h-[520px] rounded-2xl border border-[#3d414f] bg-[#0a0a12] overflow-hidden"
      style={{ perspective: 1000 }}
    >
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-[#4d4479]/40 blur-3xl" />

      <motion.svg
        viewBox={`0 0 ${W} ${H}`}
        className="relative w-full h-full"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
      >
        {/* edges */}
        {edges.map((e, i) => (
          <line
            key={e.key}
            x1={e.a.x}
            y1={e.a.y}
            x2={e.b.x}
            y2={e.b.y}
            stroke="#3d414f"
            strokeWidth="1"
            opacity="0.5"
          />
        ))}

        {/* animated signals traveling along a subset of edges */}
        {edges
          .filter((_, i) => i % 3 === 0)
          .map((e, i) => (
            <motion.circle
              key={`sig-${e.key}`}
              r="3"
              fill="#16fcfb"
              initial={{ cx: e.a.x, cy: e.a.y, opacity: 0 }}
              animate={{
                cx: [e.a.x, e.b.x],
                cy: [e.a.y, e.b.y],  
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                delay: (i % 6) * 0.4,
                ease: "easeInOut",
              }}
            />
          ))}

        {/* nodes */}
        {nodes.map((n, i) => (
          <g key={n.id}>
            <motion.circle
              cx={n.x}
              cy={n.y}
              r="9"
              fill="#07070c"
              stroke="#16fcfb"
              strokeWidth="1.5"
              initial={{ r: 8, opacity: 0.85 }}
              animate={{ r: [8, 10, 8], opacity: [0.85, 1, 0.85] }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                delay: (i % 5) * 0.3,
                ease: "easeInOut",
              }}
            />
            <circle cx={n.x} cy={n.y} r="3" fill="#97b8ff" />
          </g>
        ))}
      </motion.svg>

      <div className="absolute bottom-5 left-6 right-6 flex items-center justify-between">
        <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#16fcfb]">
          Neural Inference · Live
        </span>
        <span className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-white/40">
          Emerging Tech · AI Security
        </span>
      </div>
    </div>
  );
};

export default AINetwork;
